import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  SxProps,
  Theme,
} from "@mui/material";

import { ICreateZoneValidationState } from "../../../pages/devices/zones-setup-page/hooks/useZoneFormValidation";
import { GoToPTHandlerType } from "../../hooks/usePTController";
import CircleArrowDown from "../icons/CircleArrowDown";
import CircleArrowLeft from "../icons/CircleArrowLeft";
import CircleArrowRight from "../icons/CircleArrowRight";
import CircleArrowUp from "../icons/CircleArrowUp";
import PanInput from "../inputs/ptz/PanInput";
import TiltInput from "../inputs/ptz/TiltInput";
import ZoomInput from "../inputs/ptz/ZoomInput";

type ControlTableProps = {
  tableStyles?: SxProps<Theme>;
  manuallyControlled?: boolean;
  pan: string;
  tilt: string;
  zoom: string;
  validation?: ICreateZoneValidationState;
  showValidationAlert?: boolean;
  ptDisabled?: boolean;
  hasZoom?: boolean;
  movePtUp: () => void;
  movePtDown: () => void;
  movePtLeft: () => void;
  movePtRight: () => void;
  handleGoToPt?: GoToPTHandlerType;
  handlePanInputChange: (pan?: string) => void;
  handleTiltInputChange: (tilt?: string) => void;
  handleZoomInputChange: (zoom?: string) => void;
};

const ControlTable: React.FC<ControlTableProps> = ({
  manuallyControlled,
  pan,
  tilt,
  zoom,
  validation,
  showValidationAlert,
  handleGoToPt,
  handlePanInputChange,
  handleTiltInputChange,
  handleZoomInputChange,
  movePtDown,
  movePtLeft,
  movePtRight,
  movePtUp,
  hasZoom,
  ptDisabled,
  tableStyles = { width: "240px" },
}): JSX.Element => {
  const goToPtHandler = (): void => {
    if (!handleGoToPt) return;

    handleGoToPt(pan, tilt, zoom);
  };

  const handleMovePtDown = (): void => {
    if (!movePtDown) return;

    movePtDown();
  };

  const handleMovePtUp = (): void => {
    if (!movePtUp) return;

    movePtUp();
  };

  const handleMovePtLeft = (): void => {
    if (!movePtLeft) return;

    movePtLeft();
  };

  const handleMovePtRight = (): void => {
    if (!movePtRight) return;

    movePtRight();
  };

  return (
    <>
      <Table size="small" sx={tableStyles}>
        <TableBody>
          <TableRow
            sx={{
              "& td": {
                border: 0,
                padding: 0,
              },
              "& .small": {
                width: "32px",
                height: "32px",
              },
              "& .pan": {
                paddingLeft: ".75em",
              },
            }}
          >
            <TableCell className="small"></TableCell>
            <TableCell className="small">
              <CircleArrowUp
                disabled={ptDisabled}
                width={32}
                height={32}
                fontSize="large"
                onClick={handleMovePtUp}
              />
            </TableCell>
            <TableCell className="small"></TableCell>
            <TableCell rowSpan={2} className="pan">
              <Typography
                variant="inputLabel"
                color="otherTextTertiary.main"
                component="div"
              >
                Pan
              </Typography>

              <PanInput
                onPanChange={handlePanInputChange}
                error={validation?.pan.hasError}
                helperText={validation?.pan.errorMessage}
                disabled={ptDisabled}
                value={pan}
              />
            </TableCell>
            <TableCell rowSpan={2} className="pan">
              <Typography
                variant="inputLabel"
                color="otherTextTertiary.main"
                component="div"
              >
                Tilt
              </Typography>

              <Box>
                <TiltInput
                  onTiltChange={handleTiltInputChange}
                  error={validation?.tilt.hasError}
                  helperText={validation?.tilt.errorMessage}
                  disabled={ptDisabled}
                  value={tilt}
                />
              </Box>
            </TableCell>
            <TableCell rowSpan={2} className="pan">
              <>
                {hasZoom && (
                  <>
                    <Typography
                      variant="inputLabel"
                      color="otherTextTertiary.main"
                      component="div"
                    >
                      Zoom
                    </Typography>

                    <Box>
                      <ZoomInput
                        onZoomChange={handleZoomInputChange}
                        error={validation?.zoom.hasError}
                        helperText={validation?.zoom.errorMessage}
                        disabled={ptDisabled}
                        value={zoom}
                      />
                    </Box>
                  </>
                )}
              </>
            </TableCell>
          </TableRow>

          <TableRow
            sx={{
              "& td": {
                border: 0,
                padding: 0,
              },
              "& .small": {
                width: "32px",
                height: "32px",
              },
            }}
          >
            <TableCell className="small">
              <CircleArrowLeft
                disabled={ptDisabled}
                width={32}
                height={32}
                fontSize="large"
                onClick={handleMovePtLeft}
              />
            </TableCell>
            <TableCell className="small"></TableCell>
            <TableCell className="small">
              <CircleArrowRight
                disabled={ptDisabled}
                width={32}
                height={32}
                fontSize="large"
                onClick={handleMovePtRight}
              />
            </TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
          </TableRow>

          <TableRow
            sx={{
              "& td": {
                border: 0,
                padding: 0,
              },
              "& .small": {
                width: "32px",
                height: "32px",
              },
              "& .pan": {
                paddingLeft: ".75em",
              },
            }}
          >
            <TableCell className="small"></TableCell>
            <TableCell className="small">
              <CircleArrowDown
                disabled={ptDisabled}
                width={32}
                height={32}
                fontSize="large"
                onClick={handleMovePtDown}
              />
            </TableCell>
            <TableCell className="small"></TableCell>
            <TableCell colSpan={3} className="pan">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Tooltip
                  title={
                    !pan || !tilt || !zoom
                      ? "Please fill out all required fields"
                      : ""
                  }
                >
                  <div style={{ width: "100%" }}>
                    <Button
                      fullWidth
                      variant="contained"
                      disabled={
                        !pan ||
                        !tilt ||
                        !zoom ||
                        ptDisabled ||
                        (typeof manuallyControlled === "boolean" &&
                          !manuallyControlled)
                      }
                      onClick={goToPtHandler}
                      size="small"
                    >
                      Apply
                    </Button>
                  </div>
                </Tooltip>
              </Box>
            </TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {showValidationAlert && (
        <Box>
          {validation?.panValue.hasError && (
            <Alert severity="error">{validation?.panValue.errorMessage}</Alert>
          )}
          {validation?.tiltValue.hasError && (
            <Alert severity="error">{validation?.tiltValue.errorMessage}</Alert>
          )}
          {validation?.zoomValue.hasError && (
            <Alert severity="error">{validation?.zoomValue.errorMessage}</Alert>
          )}
        </Box>
      )}
    </>
  );
};

export default ControlTable;
